import React, { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { SUPPORTED_LANGS, useI18n } from "@/lib/i18n";

/**
 * Compact flag-pill dropdown that lets the operator pick between
 * English / Ελληνικά / العربية. Persists via the I18nProvider.
 *
 * Variants
 * ────────
 * - `compact` (default): black/dark pill — use on coloured top-nav.
 * - `light`            : white pill with border — use on the auth/sign-in
 *                        screens that sit on top of a coloured gradient.
 */
export default function LanguageSelector({ variant = "compact" }) {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside-click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const active = SUPPORTED_LANGS.find((l) => l.code === lang) || SUPPORTED_LANGS[0];

  const triggerCls =
    variant === "light"
      ? "bg-white/95 text-slate-900 border border-slate-200 hover:bg-white"
      : "bg-slate-900/10 text-current hover:bg-slate-900/20 border border-current/10";

  return (
    <div ref={wrapRef} className="relative" data-testid="lang-selector">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 h-10 px-3 rounded-lg text-sm font-semibold transition-colors ${triggerCls}`}
        data-testid="lang-selector-trigger"
        title="Change language"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="h-4 w-4 opacity-80" />
        <span className="text-base leading-none">{active.flag}</span>
        <span className="hidden sm:inline tracking-wide">{active.nativeLabel}</span>
        <ChevronDown className={`h-3.5 w-3.5 opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          className="absolute right-0 mt-2 w-56 z-[200] bg-white text-slate-900 rounded-xl shadow-xl border border-slate-200 overflow-hidden"
          data-testid="lang-selector-menu"
          role="listbox"
        >
          {SUPPORTED_LANGS.map((l) => {
            const selected = l.code === lang;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-100 ${
                    selected ? "bg-slate-50" : ""
                  }`}
                  data-testid={`lang-option-${l.code}`}
                  role="option"
                  aria-selected={selected}
                >
                  <span className="text-xl leading-none">{l.flag}</span>
                  <span className="flex-1 text-left">
                    <span className="block font-semibold">{l.nativeLabel}</span>
                    <span className="block text-[11px] text-slate-500">{l.label}</span>
                  </span>
                  {selected && <Check className="h-4 w-4 text-emerald-600" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
